import type { ItemFieldRow } from './item-field-reader';
import { applyIntegerLayerDelta } from './integer-layer-delta';
import { createFormulaReadContext } from './formula-read-context';
import { readCreaturePayloadStatuses, storedStatusRows, readStatusCasterId, isHostileAuraSelfAnchor, isDormantOwnerGatedAura } from './creature-status-reader';
import { scoreCarrierIntegerLayerArithmetic, scoreNamedIntegerFragmentInteger } from './formula-variable-resolution';
import { statusIntegerContributionValue } from './status-integer-contribution';
import type { StatusIntegerMetadata, StatusIntegerReaders, IntegerContributionSource } from './status-integer-contribution';
import { collectEquippedInventoryRows, equippedIntegerContributionValue } from './equipped-integer-contribution';
import type { EquippedIntegerDependencies, EquippedIntegerReaders } from './equipped-integer-contribution';
import { phpAdd, phpSubtract, phpJsonInteger, phpFloatToInteger } from './php-integer-arithmetic';
import type { PhpArithmetic } from './php-integer-arithmetic';
import { phpJsonString, phpJsonTruthy } from './php-json-values';
import { isPhpNumeric } from './php-decimal-values';
import { isPassiveIntegerFoldRow } from './passive-integer-fold';
import { integerCompileWorkingRow, integerCompileResult, writeIntegerComputeSlotValue,
    putIntegerCompilePath, removeIntegerCompilePath, readIntegerCompilePath } from './integer-compute-slots';

const arrayLike = (value: unknown): value is ItemFieldRow => value !== null && typeof value === 'object';
const integer = (value: PhpArithmetic): bigint => typeof value === 'bigint' ? value : phpFloatToInteger(value);
export interface IntegerRollupMetadata extends StatusIntegerMetadata {
    integerLayers: readonly string[];
    sensesNames: readonly string[];
    inventoryCapExcluded: readonly string[];
    equippedHandledElsewhere: readonly string[];
}
export interface IntegerRollupReaders extends EquippedIntegerReaders, StatusIntegerReaders {}
export interface IntegerRollupDependencies extends EquippedIntegerDependencies { metadata: IntegerRollupMetadata }

/** Actual status ownership predicate, with source-derived catalogs/constants. */
export function statusIntegerFamilyComputedByWalk(rawLayer: string,metadata: IntegerRollupMetadata): boolean {
    if (rawLayer == null || arrayLike(rawLayer)) throw new TypeError('Integer family requires a scalar string');
    const layer = phpJsonString(rawLayer);
    return layer !== 'teleport' && !layer.startsWith('speed_') && layer !== 'reach' && layer !== 'extra_attack'
        && metadata.integerLayers.includes(layer) && !metadata.inventoryCapExcluded.includes(layer) && !metadata.equippedHandledElsewhere.includes(layer);
}
/** computed._cache memo of the host damage_mitigation fragments the compile's first pass consumed (FT_DM_HOST_FRAGMENTS_CACHE_KEY). */
const DM_HOST_MEMO = ['computed','_cache','dm_host_fragments'];
const hasOwn = (value: unknown,key: string): boolean => arrayLike(value) && Object.prototype.hasOwnProperty.call(value,key);
/** _ft_normalize_layer_entries_value + array_values: null / '' / [] → none; a list → its values; anything else → one entry. */
function slotEntries(value: unknown): unknown[] {
    if (value == null || value === '') return [];
    if (!arrayLike(value)) return [value];
    if (Object.keys(value).length === 0) return [];
    return hasOwn(value,'0') ? Object.values(value) : [value];
}
/** ft_compute_damage_mitigation_chokepoint (process_int_layer_types.php, 2026-10-07): the host fragments (a pristine bucket value, else the
 *  first pass's memo + the legacy top-level slot), else the carriers; + the WALKED statuses; a computed 0 is kept as {total: 0} only while
 *  layer_value would otherwise fall back to something. */
function rollupDamageMitigation(row: Record<string,unknown>,metadata: IntegerRollupMetadata,readers: IntegerRollupReaders,hasStatuses: boolean): void {
    const layer = 'damage_mitigation', bucket = row.item_layer_types, bucketHas = hasOwn(bucket,layer);
    const top = slotEntries(row[layer]);
    let memoRaw: unknown = row; for (const key of DM_HOST_MEMO) memoRaw = hasOwn(memoRaw,key) ? (memoRaw as Record<string,unknown>)[key] : undefined;
    const memo = arrayLike(memoRaw) ? Object.values(memoRaw) : null;
    const host = bucketHas ? slotEntries((bucket as Record<string,unknown>)[layer]) : [...(memo ?? []),...top];
    if (top.length && memo === null && !bucketHas) putIntegerCompilePath(row,DM_HOST_MEMO,top);
    let total: bigint = host.length ? scoreNamedIntegerFragmentInteger(row,host as unknown as ItemFieldRow,layer,metadata,readers)
        : integer(scoreCarrierIntegerLayerArithmetic(row,layer,0,false,metadata,readers));
    if (hasStatuses) total = integer(phpAdd(total,integer(statusIntegerContributionValue(row,layer,metadata,readers).total)));
    delete row[layer];
    writeIntegerComputeSlotValue(row,layer,Number(total));
    if (total === 0n && readers.layerValue(row,layer) != null) putIntegerCompilePath(row,['computed','creature',layer,'total'],0);
}
export function rollupIntegerFamilies(row: Record<string,unknown>,metadata: IntegerRollupMetadata,readers: IntegerRollupReaders, layers: readonly string[] = metadata.integerLayers): void {
    // Initial compile preserves its ordered preflight. Delta readers borrow the
    // held rows and never deep-copy the equipped tree merely to test presence.
    const hasEquipped = layers !== metadata.integerLayers || collectEquippedInventoryRows(row,readers).length !== 0;
    const hasStatuses = Object.keys(readers.statuses(row)).length !== 0;
    for (const layer of layers) {
        if (!metadata.integerLayers.includes(layer)) continue;
        if (layer === 'teleport' || layer.startsWith('speed_') || layer === 'reach' || layer === 'extra_attack' || metadata.sensesNames.includes(layer)) continue;
        if (layer === 'damage_mitigation') { rollupDamageMitigation(row,metadata,readers,hasStatuses); continue; }
        const modifierPath = ['computed','creature','modifiers',layer];
        const sourcePath = ['computed','creature','modifier_sources',layer];
        const cpiPath = ['computed','creature','cpi',layer];
        const previousRows = slotEntries(readIntegerCompilePath(row,sourcePath)).filter(arrayLike) as Readonly<Record<string,unknown>>[];
        const cpi = readIntegerCompilePath(row,cpiPath), hasRecord = cpi != null;
        const previous = readIntegerCompilePath(row,modifierPath) ?? (previousRows.length || hasRecord ? 0 : null);
        const authored = hasOwn(row.item_layer_types,layer);
        const locked = previousRows.some(source => source.kind === 'inventory_equipped' && source.value_brace != null && !phpJsonTruthy(source.suppressed));
        let walked: PhpArithmetic = 0n, carriedFold: PhpArithmetic = 0n, carriedOther: PhpArithmetic = 0n;
        let carried: ItemFieldRow[] = [];
        if (previous !== null && !locked && isPhpNumeric(previous)) {
            for (const source of previousRows) {
                const counts = !phpJsonTruthy(source.suppressed) && isPhpNumeric(source.value);
                // Only subtract walks this browser pass re-adds. Native also subtracts
                // feats, but its paired feat pass has no browser counterpart yet.
                if (phpJsonTruthy(source.fold) || !['status_effect','inventory_equipped'].includes(phpJsonString(source.kind ?? ''))) {
                    carried.push(source);
                    if (counts) {
                        if (isPassiveIntegerFoldRow(source,layer,hasRecord)) carriedFold = phpAdd(carriedFold,phpJsonInteger(source.value));
                        else carriedOther = phpAdd(carriedOther,phpJsonInteger(source.value));
                    }
                } else if (counts) walked = phpAdd(walked,phpJsonInteger(source.value));
            }
        }
        let total: PhpArithmetic;
        if (previous !== null && !authored && !locked && isPhpNumeric(previous)) total = phpSubtract(phpJsonInteger(previous),walked);
        else if (locked && !authored) {
            // Mask only the two stale scalar slots while the synchronous reader
            // finds the unlocked host/carrier value. No actor copy is needed.
            const priorSlot = readIntegerCompilePath(row,modifierPath), hadRoot = hasOwn(row,layer), rootSlot = row[layer];
            removeIntegerCompilePath(row,modifierPath); delete row[layer];
            try { total = scoreCarrierIntegerLayerArithmetic(row,layer,0,false,metadata,readers); }
            finally {
                if (priorSlot !== undefined) putIntegerCompilePath(row,modifierPath,priorSlot);
                if (hadRoot) row[layer] = rootSlot;
            }
        } else {
            total = scoreCarrierIntegerLayerArithmetic(row,layer,0,false,metadata,readers);
            if (authored && previous !== null) {
                if (!locked && isPhpNumeric(previous)) total = phpAdd(total,phpAdd(carriedOther,
                    hasRecord ? isPhpNumeric(cpi) ? phpJsonInteger(cpi) : 0n : carriedFold));
            } else carried = [];
        }
        let statusSources: IntegerContributionSource[] = [], equippedSources: IntegerContributionSource[] = [];
        if (hasStatuses && statusIntegerFamilyComputedByWalk(layer,metadata)) {
            const contribution = statusIntegerContributionValue(row,layer,metadata,readers);
            total = phpAdd(total,integer(contribution.total)); statusSources = contribution.sources;
        }
        if (hasEquipped && !metadata.inventoryCapExcluded.includes(layer) && !metadata.equippedHandledElsewhere.includes(layer)) {
            const contribution = equippedIntegerContributionValue(row,layer,metadata,readers);
            total = contribution.locked ? contribution.total : phpAdd(total,contribution.total);
            if (contribution.locked) {
                carried = []; removeIntegerCompilePath(row,cpiPath);
                const record = readIntegerCompilePath(row,['computed','creature','cpi']);
                if (arrayLike(record) && Object.keys(record).length === 0) removeIntegerCompilePath(row,['computed','creature','cpi']);
            }
            equippedSources = contribution.sources;
        }
        delete row[layer];
        if (integer(total) === 0n && authored) putIntegerCompilePath(row,modifierPath,0);
        else writeIntegerComputeSlotValue(row,layer,Number(integer(total)));
        const retained = new Set<Readonly<Record<string,unknown>>>();
        const sources: (ItemFieldRow | IntegerContributionSource)[] = [];
        for (const group of [statusSources,equippedSources,carried]) for (const source of group) {
            const prior = previousRows.find(candidate => !retained.has(candidate) && Object.keys(source).length === Object.keys(candidate).length
                && Object.entries(source).every(([key,value]) => candidate[key] === value));
            if (prior) retained.add(prior);
            sources.push(prior ?? source);
        }
        if (!sources.length) removeIntegerCompilePath(row,sourcePath);
        else if (sources.length !== previousRows.length || sources.some((source,i) => source !== previousRows[i]))
            putIntegerCompilePath(row,sourcePath,sources);
    }
}
/** Browser integer rollup: a detached result with native arithmetic and ordered
 * source reads. Previously compiled feat integers remain carried until their pass is ported.
 * Speed, sense, reach and attack passes retain their owners.
 */
export function compileIntegerLayers(input: ItemFieldRow,metadata: IntegerRollupMetadata,readers: IntegerRollupReaders): ItemFieldRow {
    if (!arrayLike(input)) throw new TypeError('Integer rollup requires a PHP array');
    const row = integerCompileWorkingRow(input); rollupIntegerFamilies(row,metadata,readers); return integerCompileResult(row);
}
/** Actual inventory light gate invokes the entire generic rollup after statuses
 * are attached, rather than introducing a second BLDL-only calculation.
 */
export function compileInventoryLightStatusLayers(input: ItemFieldRow,metadata: IntegerRollupMetadata,readers: IntegerRollupReaders): ItemFieldRow {
    if (!arrayLike(input)) throw new TypeError('Inventory light compile requires a PHP array');
    const row = integerCompileWorkingRow(input);
    if ((readers.itemField(row,'item_type') ?? '') === 'light_source' && Object.keys(readers.statuses(row)).length !== 0) rollupIntegerFamilies(row,metadata,readers);
    return integerCompileResult(row);
}
export function createIntegerRollupCompiler(deps: IntegerRollupDependencies) {
    const context = createFormulaReadContext(deps);
    const reference = (id: string): ItemFieldRow | null => {
        deps.trace?.(['reference',id]); const value = deps.resolveCarrierRef(id);
        if (value !== null && !arrayLike(value)) throw new TypeError('Rollup reference requires a PHP array or null');
        return value;
    };
    const readers: IntegerRollupReaders = {...context,resolveCarrierRef:reference,statuses:row=>readCreaturePayloadStatuses(row,context.layerValue,reference),
        queryFullItem:id=>{deps.trace?.(['full-item',id]); return deps.queryFullItem(id);},
        itemName:id=>{deps.trace?.(['name',id]); if (!deps.resolveItemName) throw new TypeError('Rollup item name requires an explicit lookup'); return deps.resolveItemName(id);},
    };
    const deltaReaders: IntegerRollupReaders = {...readers,statuses:row=>storedStatusRows(row as Record<string,unknown>,'target').flatMap(status => {
            const id = status.status_item_id ?? status.status_arr;
            const rawCatalog = readIntegerCompilePath(row,['catalog']);
            const catalog = arrayLike(rawCatalog) ? rawCatalog as Record<string,unknown> : {};
            const hit = typeof id === 'string' ? catalog[id] ?? catalog[id.toUpperCase()] ?? catalog[id.toLowerCase()] : null;
            const definition = arrayLike(status.status_arr) ? status.status_arr
                : arrayLike(hit) && readIntegerCompilePath(hit,['items','item_id']) != null
                    && (readIntegerCompilePath(hit,['item_layer_types']) != null || readIntegerCompilePath(hit,['computed']) != null) ? hit
                : typeof id === 'string' && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(id) ? reference(id) : null;
            const caster = readStatusCasterId(status);
            const hydrated = definition === status.status_arr && caster === status.status_caster_id ? status
                : {...status,status_arr:definition ?? {items:{item_id:status.status_item_id}},...(caster === undefined ? {} : {status_caster_id:caster})};
            return isHostileAuraSelfAnchor(definition,hydrated.status_aoe_source,caster,hydrated.status_target_id,context.layerValue)
                || isDormantOwnerGatedAura(hydrated,context.layerValue,undefined,row) ? [] : [hydrated];
        })};
    return {compile:(row:ItemFieldRow)=>compileIntegerLayers(row,deps.metadata,readers),
        delta:(row:Record<string,unknown>,layers:readonly string[])=>applyIntegerLayerDelta(row,layers,deps.metadata,deltaReaders),
        light:(row:ItemFieldRow)=>compileInventoryLightStatusLayers(row,deps.metadata,readers),
        statusFamily:(layer:string)=>statusIntegerFamilyComputedByWalk(layer,deps.metadata)};
}
